import config from 'config';
import firebase from 'libs/firebase';

const handleResetPassword = (newPassword: string, actionCode: string): Promise<any> => {
  let accountEmail: string = '';
  const auth = firebase.auth();
  // Verify the password reset code is valid.
  return auth
    .verifyPasswordResetCode(actionCode)
    .then((email: string) => {
      accountEmail = email;
      // Save the new password.
      return auth
        .confirmPasswordReset(actionCode, newPassword)
        .then(() => {
          // Password reset has been confirmed and new password updated.

          return auth.signInWithEmailAndPassword(accountEmail, newPassword);
        })
        .catch((err: Error) => {
          // Error occurred during confirmation. The code might have expired or the
          // password is too weak.
          console.log('Error occured => ', err.message);
        });
    })
    .catch((err: Error) => {
      // Invalid or expired action code. Ask user to try to reset the password
      // again.
      console.log('Error occured => ', err.message);
    });
};

const handleVerifyEmail = (actionCode: string): Promise<any> => {
  const auth = firebase.auth();
  // Try to apply the email verification code.
  return auth
    .applyActionCode(actionCode)
    .then(() => {
      // Email address has been verified.
      console.log('Email address verified');
    })
    .catch((err: Error) => {
      // Code is invalid or expired. Ask the user to verify their email address
      // again.
      console.error('Error occurred. Inspect =>', err.message);
    });
};

/**
 * Login a user
 * @param {Object} values
 * @return {Promise}
 */
export const handleLogin = (values: {
  [key: string]: string | boolean | string[] | undefined | object;
}): Promise<any> => {
  const { email, password } = values;

  return firebase.auth().signInWithEmailAndPassword(email as string, password as string);
};

/**
 * Handle forgot password for a user
 * @param {Object} values
 * @return {Promise}
 */

export const handleForgotPassword = (values: {
  [key: string]: string | boolean | undefined | string[] | object;
}) => {
  console.log({ values, env: config });

  const { email } = values;

  return firebase.auth().sendPasswordResetEmail(email as string);
};

/**
 * Logout a user
 * @param {void}
 * @return {Promise}
 */

export const handleLogout = () => {
  const user = firebase.auth().currentUser;

  if (!user) {
    return Promise.reject(user);
  }

  return firebase
    .auth()
    .signOut()
    .then(() => {
      // Sign-out successful.
      console.log('User logged out');
      window.location.href = '/login';
      localStorage.clear();
    })
    .catch((err: Error) => {
      // An error happened.
      console.error('Error occurred. Inspect =>', err.message);
    });
};

/**
 * Update a user password only when logged in
 * @param {void}
 * @return {Promise}
 */

export const handleUpdatePassword = (values: {
  [key: string]: string | boolean | string[] | undefined;
}) => {
  const user = firebase.auth().currentUser;

  /* eslint-disable-next-line camelcase */
  const { newPassword, confirmNewPassword } = values;

  /* eslint-disable-next-line camelcase */
  if (!user || newPassword !== confirmNewPassword) {
    return Promise.reject(new Error('something unexpected occured'));
  }

  /* eslint-disable-next-line camelcase */
  return user
    .updatePassword(newPassword as string)
    .then(() => {
      // Updated successfully.
      console.log('Password updated!');
    })
    .catch((err: Error) => {
      // An error happened.
      console.error('Error occurred. Inspect =>', err.message);
    });
};

/**
 * Gets current user info
 * @return {Function}
 */
export const getCurrentAuthUser = () => {
  const { currentUser } = firebase.auth();

  return currentUser;
};

/**
 * Update a user password only during a reset
 * @param {String} actionCode
 * @param {String} mode
 * @return {Function}
 */

export const getAuthActionRoutine = ({
  actionCode,
  mode,
  history,
}: {
  [key: string]: any;
}) => {
  if (mode === 'recoverEmail') {
    return (values: {
      [key: string]: string | boolean | string[] | undefined | object;
    }) => Promise.reject(values);
  }

  return function handleAuthAction(values: {
    [key: string]: string | boolean | string[] | undefined | object;
  }) {
    if (mode === 'resetPassword') {
      /* eslint-disable-next-line camelcase */
      const { newPassword, confirmNewPassword } = values;

      /* eslint-disable-next-line camelcase */
      if (newPassword !== confirmNewPassword) {
        return Promise.reject(new Error('Passwords do not match'));
      }

      /* eslint-disable-next-line camelcase */
      return handleResetPassword(newPassword as string, actionCode as string).then(() => {
        history.replace('/dashboard');
      });
    }

    return handleVerifyEmail(actionCode);
  };
};
